import Box from '@mui/material/Box';
import { containerStyles } from './styles';
import { ConversationPropTypes } from '../../pages/admin/EstheticianMessaging/propTypes/Conversation';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import TypingArea from '../../pages/admin/EstheticianMessaging/Conversation/TypingArea';
import { useMessagesForConversationSID } from 'providers/TwilioProvider/hooks/public/useMessagesForConversationSID';
import { useTwilio } from 'providers/TwilioProvider/hooks/public/useTwilio';
import { useChatAutoScroll } from './hooks/useChatAutoScroll';
import Message from './Message';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import PompConciergeMessage from './PompConciergeMessage';
import { useSideBarAwareTheme } from 'providers';
import { EmptyChatClient } from './empty-chat-client';

const Chat = ({
  selectedConversation,
  isRenderedWithinDrawer,
  userId,
  estheticianId,
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { mobileNavHeaderHeight } = useSideBarAwareTheme();

  const { identity, markConversationAsRead } = useTwilio();

  const {
    loading,
    messages,
    isOutgoingMessage,
    initializeAndBindConversationClient,
  } = useMessagesForConversationSID(selectedConversation?.sid);

  const [focus] = useState(false);

  const { containerRef, retriggerAutoScrollOnImageLoad } = useChatAutoScroll({
    loading,
    selectedConversation,
  });

  useEffect(() => {
    if (!selectedConversation || selectedConversation.source === 'sdk') return;
    initializeAndBindConversationClient(selectedConversation);
  }, [selectedConversation]);

  /**
   * Mark the conversation as read once we've scrolled to the bottom
   * and there is a message to read.
   */
  useEffect(() => {
    if (messages && selectedConversation && messages.length > 0)
      markConversationAsRead(selectedConversation.sid);
  }, [selectedConversation, messages]);

  const emptyClientMessaging =
    !selectedConversation?.lastMessageCreated && isRenderedWithinDrawer;

  return (
    <Choose>
      <When condition={selectedConversation}>
        <Box
          sx={containerStyles(
            !!selectedConversation,
            !isRenderedWithinDrawer ? mobileNavHeaderHeight : 0,
            isRenderedWithinDrawer
          )}
        >
          <If condition={emptyClientMessaging}>
            <EmptyChatClient />
          </If>

          <Box
            sx={{ flex: emptyClientMessaging ? 0 : 1, overflowY: 'scroll' }}
            ref={containerRef}
          >
            <Box sx={{ flex: 1 }} />
            <If condition={messages?.length > 0}>
              <For each="message" index="index" of={messages}>
                <Message
                  key={message.sid}
                  message={message}
                  uniqueName={selectedConversation.uniqueName}
                  isOutgoing={isOutgoingMessage(message)}
                  emitImageLoaded={retriggerAutoScrollOnImageLoad}
                />
              </For>
            </If>
          </Box>

          <TypingArea
            conversation={selectedConversation}
            tablet={isTablet}
            identity={identity}
            esthetician={true}
            forwardFocus={focus}
            closeSearch={false}
            userId={userId}
            estheticianId={estheticianId}
          />
        </Box>
      </When>

      <Otherwise>
        <Box
          ref={containerRef}
          sx={containerStyles(
            !!selectedConversation,
            !isRenderedWithinDrawer ? mobileNavHeaderHeight : 0,
            isRenderedWithinDrawer
          )}
        >
          <Box sx={{ flex: 1 }} />

          <PompConciergeMessage
            message="Start a chat with one of your clients"
            isOutgoing={false}
          />

          <TypingArea
            disabled={true}
            conversation={selectedConversation}
            tablet={isTablet}
            identity={identity}
            esthetician={true}
            forwardFocus={focus}
            closeSearch={false}
            userId={userId}
            estheticianId={estheticianId}
          />
        </Box>
      </Otherwise>
    </Choose>
  );
};

Chat.propTypes = {
  selectedConversation: ConversationPropTypes,
  isRenderedWithinDrawer: PropTypes.bool,
  userId: PropTypes.string,
  estheticianId: PropTypes.string,
};

export default Chat;
